import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Upload as UploadIcon,
  ContentPaste as PasteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../../shared/components';
import { useMe } from '../../../shared/hooks/useMe';
import { canReadContract, canWriteContract } from '../../../shared/permissions/capabilities';
import { colors, typography } from '../../../shared/styles/theme';
import { getSessions } from '../utils/contractApi';
import type { SessionListItem } from '../types/contracts';
import ContractReviewSubmissionList from './ContractReviewSubmissionList';

const tealButtonSx = {
  backgroundColor: colors.primaryLight,
  color: colors.text.light,
  textTransform: 'none' as const,
  fontWeight: typography.fontWeight.semibold,
  borderRadius: 2,
  px: 2,
  py: 1,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: colors.sidebar.selected,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: colors.border.medium,
    color: colors.text.light,
  },
};

const HERO_BG = '#E6F7F7';
const HERO_BORDER = 'rgba(72, 161, 173, 0.35)';

const ContractReviewHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { me } = useMe();
  const canWrite = canWriteContract(me);
  const canRead = canReadContract(me);

  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSessions({ page: 1, page_size: 5 });
      setSessions(res.sessions);
    } catch {
      setError('Failed to load recent submissions.');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const openSession = (uuid: string) => {
    navigate(`/contract-review/${uuid}`);
  };

  return (
    <PageLayout>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Upload Contract card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${HERO_BG} 0%, #d4f0f0 100%)`,
            border: `1px solid ${HERO_BORDER}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <UploadIcon sx={{ color: colors.text.light, fontSize: 32 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.bold,
              fontSize: { xs: typography.fontSize['3xl'], sm: '1.85rem' },
              color: '#111827',
              mb: 1.5,
            }}
          >
            Upload Contract
          </Typography>
          <Typography
            sx={{
              color: '#374151',
              fontSize: typography.fontSize.lg,
              lineHeight: typography.lineHeight.relaxed,
              mb: 3,
            }}
          >
            Submit a new contract for automated review and analysis. Our AI-powered system will identify
            risks, compliance issues, and provide detailed insights.
          </Typography>
          {canWrite ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/contract-review/upload')}
              sx={{ ...tealButtonSx, py: 1.25, px: 3, fontSize: typography.fontSize.lg }}
            >
              Upload Now
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              You do not have permission to upload contracts.
            </Typography>
          )}
        </Paper>

        {/* Paste Clauses card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${HERO_BG} 0%, #d4f0f0 100%)`,
            border: `1px solid ${HERO_BORDER}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <PasteIcon sx={{ color: colors.text.light, fontSize: 32 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.bold,
              fontSize: { xs: typography.fontSize['3xl'], sm: '1.85rem' },
              color: '#111827',
              mb: 1.5,
            }}
          >
            Paste Contract Clauses
          </Typography>
          <Typography
            sx={{
              color: '#374151',
              fontSize: typography.fontSize.lg,
              lineHeight: typography.lineHeight.relaxed,
              mb: 3,
            }}
          >
            Copy and paste individual security clauses for AI assessment without uploading a full document.
          </Typography>
          {canWrite ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/contract-review/paste')}
              sx={{ ...tealButtonSx, py: 1.25, px: 3, fontSize: typography.fontSize.lg }}
            >
              Paste Clauses
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              You do not have permission to submit clauses.
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Recent submissions */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.card,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontWeight: typography.fontWeight.bold,
              fontSize: typography.fontSize.xl,
              color: '#111827',
            }}
          >
            Recent Submissions
          </Typography>
          {(canRead || canWrite) && (
            <Button
              variant="contained"
              onClick={() => navigate('/contract-review/review')}
              sx={{ ...tealButtonSx, minHeight: 36 }}
            >
              View All
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} sx={{ color: colors.primaryLight }} />
          </Box>
        ) : sessions.length === 0 ? (
          <Typography sx={{ color: colors.text.secondary, py: 2 }}>
            No submissions yet. Upload a contract to see it here.
          </Typography>
        ) : (
          <ContractReviewSubmissionList sessions={sessions} onOpen={openSession} />
        )}
      </Paper>
    </PageLayout>
  );
};

export default ContractReviewHomePage;
